import { buildGlsInitPath } from './build-gls-init-path';
import {
  buildGlsInitPayload,
  BuildGlsInitPayloadInput,
} from './build-gls-init-payload';
import { getGlsCachedGameDetailsFromStore } from './get-gls-cached-game-details-from-store';
// Absolute, not relative: the harness aliases this `@/` specifier.
import { getGlsBaseUrl } from '@/api/gls/get-gls-base-url';
import { GlsInitResponse } from './types';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import axios from 'axios';
import { HEADERS } from '@/utilities/constants/metadata';

export type FetchGlsInitInput = BuildGlsInitPayloadInput & {
  region?: string;
  shouldSkipPrismic?: boolean;
};

export const fetchGlsInit = async (
  input: FetchGlsInitInput,
): Promise<GlsInitResponse> => {
  const path = buildGlsInitPath();
  const { shouldSkipPrismic, ...restInput } = input ?? {};
  const cachedGameDetails = shouldSkipPrismic
    ? getGlsCachedGameDetailsFromStore()
    : null;

  if (shouldSkipPrismic && !cachedGameDetails) {
    Logger.logRumError({
      name: '[GW][FETCH_GLS_INIT] Missing cached game details for non-Prismic GLS',
      useCase: DdEventCategory.GameLaunch,
      eventType: DdEventType.Error,
      context: { gameUid: input.gameUid },
    });
  }

  const payload = buildGlsInitPayload({
    ...restInput,
    usePrismic: cachedGameDetails ? false : (restInput.usePrismic ?? true),
    ...(cachedGameDetails
      ? {
          gameUid: cachedGameDetails.gameUid,
          provider: cachedGameDetails.provider,
          providerRef: cachedGameDetails.providerRef,
          ...(cachedGameDetails.directLaunchParams
            ? { directLaunchParams: cachedGameDetails.directLaunchParams }
            : {}),
        }
      : {}),
  });

  Logger.logRumAction({
    name: '[GW][FETCH_GLS_INIT] Begin',
    useCase: DdEventCategory.GameLaunch,
    eventType: DdEventType.Info,
  });

  const instance = axios.create({
    headers: {
      [HEADERS.CACHE_CONTROL]: 'no-store, no-cache',
    },
  });
  const response = await instance.post<GlsInitResponse>(path, payload, {
    baseURL: getGlsBaseUrl(input.region),
  });

  const body = response.data;

  if (body?.error) {
    Logger.logRumError({
      name: '[GW][FETCH_GLS_INIT] Failed',
      useCase: DdEventCategory.GameLaunch,
      eventType: DdEventType.Error,
      context: {
        errorCode: body.error.errorCode,
        errorMessage: body.error.message,
        requestId: body.data?.requestId,
      },
    });
    return body;
  }

  Logger.logRumAction({
    name: '[GW][FETCH_GLS_INIT] Success',
    useCase: DdEventCategory.GameLaunch,
    eventType: DdEventType.Success,
  });

  return body;
};
